import './polyfills';
import './Application/pages';
import { bootstrap } from '@microsoft/portal-app/lib/bootstrap';
import { setOverrideSettings } from '@microsoft/portal-app/lib/shared/override-settings/overrideSettings';
import { ErrorBoundary } from '@uifabric/portal-ux/lib/ErrorBoundary';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { registerAccountFlags } from './Application/AccountFlags';
import { AccountMain } from './Application/Main';
import { auth } from './auth';
import { ErrorPage } from './ErrorPage';
import { history } from './redux/configureStore';
import { settingsManager } from './shared/settings';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { initializeIcons, ThemeProvider } from '@fluentui/react';
import { getTelemetry } from './Telemetry/Telemetry';
import { TelemetryProvider } from '@iamexperiences/ecos-telemetry';
import { appTheme } from './theme';
import '@amx/ux-twoui/public/amcIcons.css';
import '@amx/ux-twoui/public/common-public/reset.css';
import { initializeIconsTwoUi } from '@amx/ux-twoui';
import { AuthenticatedTemplate, AuthProvider, CustomNavigationClient } from '@iamexperiences/feature-auth';
import { experimentClient } from './Experiment/experimentClient';
import { ExperimentClientProvider } from '@iamexperiences/client-experiment';
import { AppProvider } from './Application/AppProvider';
import { NAVIGATION_LINKS } from './shared/constants';

const isAnonymousPage = [
  // Terms of use interrupt
  NAVIGATION_LINKS.termsOfUse.subRoutePaths!.consent.link,
].includes(window.location.pathname);

const settings = settingsManager.getAll();
setOverrideSettings({
  ...settings,
  authImplementation: {
    clientId: auth.clientId,
    user: () => auth.user,
    login: auth.login,
    logout: auth.logout,
    acquireToken: async (audience: string | string[]) =>
      await auth.acquireToken({ scopes: audience instanceof Array ? audience : [audience] }),
  },
  // Don't set value for "log" as portal-app sends telemetry for network calls by calling "log" method
  // but the app already used 1DS to auto-track network calls
  telemetryImplementation: {
    error: (...args) => {
      const telemetry = getTelemetry();
      return telemetry?.error(...args);
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // set the query data to stale after 5 minutes. Before it becomes stale, refetch won't occur
      retry: false,
    },
  },
});

// add this to support client-side routing with MSAL
const navigationClient = new CustomNavigationClient(history);
auth.setNavigationClient(navigationClient);

initializeIcons('/fonts/');
initializeIconsTwoUi();

const render = () => {
  ReactDOM.render(
    <ErrorBoundary component={ErrorPage}>
      <TelemetryProvider telemetry={getTelemetry()}>
        <AppProvider>
          <AuthProvider auth={auth}>
            {isAnonymousPage ? (
              <QueryClientProvider client={queryClient}>
                <ThemeProvider style={{ height: '100%' }} theme={appTheme}>
                  <AccountMain />
                </ThemeProvider>
              </QueryClientProvider>
            ) : (
              <AuthenticatedTemplate>
                <ExperimentClientProvider experimentClient={experimentClient}>
                  <QueryClientProvider client={queryClient}>
                    <ThemeProvider style={{ height: '100%' }} theme={appTheme}>
                      <AccountMain />
                    </ThemeProvider>
                    {/* Uncomment only for local debugging */}
                    {/* <ReactQueryDevtools initialIsOpen={false} /> */}
                  </QueryClientProvider>
                </ExperimentClientProvider>
              </AuthenticatedTemplate>
            )}
          </AuthProvider>
        </AppProvider>
      </TelemetryProvider>
    </ErrorBoundary>,
    document.getElementById('root'),
  );
};

Promise.resolve(true)
  .then(() => registerAccountFlags())
  .then(() => bootstrap())
  .then((shouldContinue: boolean) => (shouldContinue ? render() : undefined));
